import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.categorySlug === movie.categorySlug)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">影片不存在</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您查找的影片未能找到</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-colors"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${movie.title} ${movie.year} - 6080新视觉影院在线观看`;
  const pageDesc = `6080新视觉影院提供${movie.title}高清免费在线观看。${movie.description}导演${movie.director}，主演${movie.actors.join('、')}，${movie.category}不卡顿流畅播放。`;

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: movie.category,
        item: typeof window !== 'undefined' ? window.location.origin + `/category/${movie.categorySlug}` : `/category/${movie.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: movie.title,
        item: typeof window !== 'undefined' ? window.location.origin + `/article/${movie.id}` : `/article/${movie.id}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - ${movie.year}${movie.category}`,
    description: movie.description,
    author: {
      '@type': 'Organization',
      name: '6080新视觉影院',
    },
    datePublished: movie.releaseDate,
    image: movie.posterUrl,
    articleBody: movie.longDescription,
    publisher: {
      '@type': 'Organization',
      name: '6080新视觉影院',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbListSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${movie.backdropUrl})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
          </div>

          <div className="w-full px-4 md:px-6">
            <div className="relative -mt-32 md:-mt-40 z-10">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 mx-auto md:mx-0 w-40 md:w-56">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden shadow-2xl">
                    <img
                      src={movie.posterUrl}
                      alt={`${movie.title} ${movie.year} ${movie.category} 海报`}
                      title={`${movie.title} ${movie.category}在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 pt-2 text-center md:text-left">
                  <nav className="flex items-center justify-center md:justify-start gap-2 text-sm text-background-50/60 mb-4" aria-label="面包屑导航">
                    <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <Link to={`/category/${movie.categorySlug}`} className="hover:text-background-50 transition-colors">{movie.category}</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <span className="text-background-50">{movie.title}</span>
                  </nav>
                  <h1 className="text-2xl md:text-4xl font-bold text-background-50 mb-3 font-[var(--font-heading)]">
                    {movie.title}
                  </h1>
                  <p className="text-sm md:text-base text-background-50/60 mb-2">{movie.titleEn}</p>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-4">
                    <span className="px-2.5 py-1 text-xs font-semibold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                      {movie.rating} 分
                    </span>
                    {movie.isNew && (
                      <span className="px-2.5 py-1 text-xs font-medium bg-green-500 text-white rounded-full whitespace-nowrap">
                        最新上映
                      </span>
                    )}
                    {movie.isHot && (
                      <span className="px-2.5 py-1 text-xs font-medium bg-red-500 text-white rounded-full whitespace-nowrap">
                        热门推荐
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-5">
                    <div className="flex items-center gap-1.5 text-sm text-background-50/70">
                      <i className="ri-user-line"></i>
                      <span>{movie.director}（导演）</span>
                    </div>
                    <span className="text-background-50/40 hidden sm:inline">|</span>
                    <div className="flex items-center gap-1.5 text-sm text-background-50/70">
                      <i className="ri-group-line"></i>
                      <span>{movie.actors.slice(0, 3).join(' / ')}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm text-background-50/60">
                    <span>{movie.region}</span>
                    <span>·</span>
                    <span>{movie.language}</span>
                    <span>·</span>
                    <span>{movie.duration}</span>
                    <span>·</span>
                    <span>{movie.releaseDate} 上映</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <article>
                  <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 font-[var(--font-heading)]">
                    剧情简介
                  </h2>
                  <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-3">
                    {movie.longDescription.split('\n').filter(Boolean).map((para, i) => (
                      <p key={i}>{para.trim()}</p>
                    ))}
                  </div>
                </article>

                <div className="mt-8">
                  <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4 font-[var(--font-heading)]">
                    影片标签
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-lg p-5 sticky top-20">
                  <h3 className="font-semibold text-foreground-950 mb-4">影片信息</h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">导演</dt>
                      <dd className="text-foreground-900 font-medium">{movie.director}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">主演</dt>
                      <dd className="text-foreground-900 font-medium text-right max-w-[180px]">{movie.actors.join('、')}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">类型</dt>
                      <dd className="text-foreground-900 font-medium">{movie.category}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">地区</dt>
                      <dd className="text-foreground-900 font-medium">{movie.region}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">语言</dt>
                      <dd className="text-foreground-900 font-medium">{movie.language}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">片长</dt>
                      <dd className="text-foreground-900 font-medium">{movie.duration}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">上映日期</dt>
                      <dd className="text-foreground-900 font-medium">{movie.releaseDate}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">评分</dt>
                      <dd className="text-accent-600 font-bold">{movie.rating}</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-foreground-500">观看量</dt>
                      <dd className="text-foreground-900 font-medium">{(movie.viewCount / 10000).toFixed(0)} 万</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-12 md:mt-16 pb-12">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5" data-product-shop="">
                  {relatedMovies.map((rm) => (
                    <Link
                      key={rm.id}
                      to={`/article/${rm.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                        <img
                          src={rm.posterUrl}
                          alt={rm.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                            {rm.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-accent-600 transition-colors line-clamp-1">
                        {rm.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{rm.year} · {rm.category}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}